import { Breadcrumb, Tooltip } from "antd";
import React, { useContext, useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router";
import { HomeContext } from "../containers/Home";
import useHandleContextMenu from "../hooks/useHandleContextMenu";
import { useReaderTopics } from "../hooks/useHelpCentre";
import { MainMenuItems } from "../services/MainMenu";
import { getAuditsList } from "../services/api-server/audits";
import { getIncidentReportList } from "../services/api-server/incident_report";
import { getLessonLearnedList } from "../services/api-server/lesson_learned";
import { getMoCList } from "../services/api-server/moc";
import { getOFIList } from "../services/api-server/ofi";
import { getOperations } from "../services/api-server/operations";
import { getPhaseItem } from "../services/api-server/phases";
import { getProjects } from "../services/api-server/projects";
import { getTasks } from "../services/api-server/tasks";
import { getWells } from "../services/api-server/wells";
import { useProjectStore } from "../stores/projectStore";
import { getNonConformanceList } from "../services/api-server/non_conformance";
import { HELP_CENTRE_PATH, helpCrumbTitle } from "../utils/helpCentre/content";

function BreadcrumbComponent() {
	const context: any = useContext(HomeContext);
	const location: any = useLocation();
	const navigate = useNavigate();

	const [menuRoutes, setMenuRoutes]: any = useState<any>(null);
	const [breadCrumb, setBreadCrumb]: any = useState<any>([]);

	const { handleContextMenu } = useHandleContextMenu();

	const { initialMainMenu, initialExtraMenu, otherMenu } = MainMenuItems();

	// Get project from store at component level
	const project = useProjectStore((state) => state.project);

	// The same cached topics the Help Centre pages read, for topic and section
	// titles. Only on those pages, so the cache can't rebuild other crumbs.
	const isHelpTopicPath = location.pathname.startsWith(
		`${HELP_CENTRE_PATH}/topics/`
	);
	const { topics: cachedHelpTopics } = useReaderTopics(isHelpTopicPath);
	const helpTopics = isHelpTopicPath ? cachedHelpTopics : undefined;

	useEffect(() => {
		// Recursive function to flatten menu items and filter out ones with the "to" prop
		const flattenMenuItemsWithToProp = (items: any) => {
			return items.flatMap((item: any) => {
				const menuWithTo = item.to ? [{ ...item, to: item.to }] : [];
				const menuChildren = item.children
					? flattenMenuItemsWithToProp(item.children)
					: [];
				const specialChildren = item.child
					? flattenMenuItemsWithToProp(item.child)
					: [];
				return [...menuWithTo, ...menuChildren, ...specialChildren];
			});
		};

		const flatMenuItemsWithToProp = flattenMenuItemsWithToProp([
			...initialMainMenu,
			...initialExtraMenu,
			...otherMenu,
		]);
		setMenuRoutes(flatMenuItemsWithToProp);
	}, [context]);

	const getBreadcrumbNameFromPath = async (
		path: any,
		solo: any,
		index: any
	) => {
		const keyPathToText = (keyName: any) => {
			if (keyName) {
				// Capitalize the first letter of the keyName
				const capitalizedKeyName =
					keyName.charAt(0).toUpperCase() + keyName.slice(1);
				const removedDash = capitalizedKeyName.replace(/-/g, " ");
				return removedDash;
			} else return "undefined";
		};

		let splittedPath = path.split("/");
		// Help Centre topics and sections are named from the cached topics, and
		// by their key until those load. Returned here, so the menu lookup below
		// can't rename them.
		if (splittedPath[1] === HELP_CENTRE_PATH.slice(1)) {
			return (
				helpCrumbTitle(splittedPath, index, helpTopics) ?? keyPathToText(solo)
			);
		}
		// Lists of unique predefined paths
		const uniquePaths = [
			"preview",
			"add-new-project",
			"well-settings",
			"operation-settings",
			"project-settings",
		];
		if (splittedPath[1] === "projects" && !uniquePaths.includes(solo)) {
			if (index == 1) {
				// solo = context.currentProject?.project_name;
				try {
					const project: any = await getProjects(context.currentTenantKey, {
						project_code: solo,
					});
					solo = project[0]?.project_name;
				} catch (error) {
					console.log("Fail to generate correct breadcrumb", error);
					solo = "undefined";
				}
			}
			if (index == 2) {
				// solo = context.currentOperation?.operation_name;
				if (solo === "multi-asset" || solo === "well-examination") {
					return null;
				} else {
					try {
						const operation: any = await getOperations(
							context.currentTenantKey,
							{
								operation_code: solo,
							}
						);
						solo = operation[0]?.operation_name;
					} catch (error) {
						console.log("Fail to generate correct breadcrumb", error);
						solo = "undefined";
					}
				}
			}
			if (index == 3) {
				// solo = context.currentPhase?.phase_name;
				try {
					const phase: any = await getPhaseItem(context.currentTenantKey, solo);
					solo = phase[0]?.phase_name;
				} catch (error) {
					console.log("Fail to generate correct breadcrumb", error);
					solo = "undefined";
				}
			}
			if (index == 4) {
				// solo = context.currentTask?.task_name;
				try {
					const task: any = await getTasks(context.currentTenantKey, {
						_id: solo,
					});
					console.log("task", task);
					solo = task[0]?.task_name;
				} catch (error) {
					console.log("Fail to generate correct breadcrumb", error);
					solo = "undefined";
				}
			}
		} else if (splittedPath[1] === "lessons-learned") {
			if (index == 1) {
				try {
					const report: any = await getLessonLearnedList({
						_id: solo,
					});
					solo = report[0]?.title || "undefined";
				} catch (error) {
					console.log("Fail to generate correct breadcrumb", error);
					solo = "undefined";
				}
			}
		} else if (splittedPath[1] === "wells") {
			if (index == 1) {
				// fetch the well name from the api
				try {
					const well: any = await getWells(context.currentTenantKey, {
						_id: solo,
					});
					solo = well[0]?.well_name;
				} catch (error) {
					console.log("Fail to generate correct breadcrumb", error);
					solo = "undefined";
				}
			}
		} else if (splittedPath[1] === "reporting") {
			if (index == 1) {
				if (solo === "audit-stream") {
					solo = "Audits";
				} else if (solo === "incident-stream") {
					solo = "Incident Reports";
				} else if (solo === "moc-stream") {
					solo = "Management of Change";
				} else if (solo === "ofi-stream") {
					solo = "Opportunity for Improvement";
				} else if (solo === "non-conformance-stream") {
					solo = "Non conformance report stream";
				} else if (solo === "my-reports") {
					solo = "My reports";
				}
			}
			if (index == 2) {
				const reportId = solo;
				if (splittedPath.includes("audit-stream")) {
					try {
						const report: any = await getAuditsList({
							_id: reportId,
						});
						solo = report[0]?.title || "undefined";
					} catch (error) {
						console.log("Fail to generate correct breadcrumb", error);
						solo = "undefined";
					}
				} else if (splittedPath.includes("incident-stream")) {
					try {
						const report: any = await getIncidentReportList({
							_id: reportId,
						});
						solo = report[0]?.title || "undefined";
					} catch (error) {
						console.log("Fail to generate correct breadcrumb", error);
						solo = "undefined";
					}
				} else if (splittedPath.includes("moc-stream")) {
					try {
						const report: any = await getMoCList({
							_id: reportId,
						});
						solo = report[0]?.title || "undefined";
					} catch (error) {
						console.log("Fail to generate correct breadcrumb", error);
						solo = "undefined";
					}
				} else if (splittedPath.includes("ofi-stream")) {
					try {
						const report: any = await getOFIList({
							_id: reportId,
						});
						solo = report[0]?.title || "undefined";
					} catch (error) {
						console.log("Fail to generate correct breadcrumb", error);
						solo = "undefined";
					}
				} else if (splittedPath.includes("non-conformance-stream")) {
					try {
						const report: any = await getNonConformanceList({
							_id: reportId,
						});
						solo = report[0]?.title || undefined;
					} catch (error) {
						solo = undefined;
					}
				}
			}
		} else {
			solo = keyPathToText(solo);
		}

		const route = menuRoutes.find((route: any) => route?.to?.includes(solo));
		return route ? route.proptitle : keyPathToText(solo);
	};

	const findChildRoute = (parentPath: any, currentPath: any) => {
		const parentRoute = menuRoutes.find(
			(route: any) => route.path === parentPath
		);
		if (!parentRoute || !parentRoute.children) return null;
		return parentRoute.children.find((childRoute: any) =>
			currentPath.startsWith(parentPath + childRoute.path)
		);
	};

	useEffect(() => {
		const buildBreadcrumbs = async () => {
			if (menuRoutes) {
				const pathSnippets = location.pathname.split("/").filter((i: any) => i);

				const breadcrumbItems = [
					{
						key: "home",
						title: "Home",
						href: "/dashboard",
						index: 1,
					},
				];

				const breadcrumbItemsAdd = await Promise.all(
					pathSnippets.map(async (slicedPath: any, index: any) => {
						let url = `/${pathSnippets.slice(0, index + 1).join("/")}`;

						if (index === 2 && pathSnippets[0] === "projects") {
							if (project?.project_scope === "multi-asset") {
								url = `/${pathSnippets.slice(0, index).join("/")}`;
							}
						}

						if (
							index === 3 &&
							pathSnippets[0] === "projects" &&
							project?.project_scope === "multi-asset"
						) {
							//replace index 2 with multi-asset
							pathSnippets[2] = "multi-asset";
							url = `/${pathSnippets.slice(0, index + 1).join("/")}`;
						}

						const breadcrumbName = await getBreadcrumbNameFromPath(
							url,
							slicedPath,
							index
						);
						if (breadcrumbName) {
							return {
								key: breadcrumbName + index,
								title: breadcrumbName,
								href: url,
								index: index + 2,
							};
						}
						return null;
					})
				);

				const cleanedItems = breadcrumbItemsAdd.filter(
					(item): item is (typeof breadcrumbItems)[0] => item !== null
				);

				setBreadCrumb([...breadcrumbItems, ...cleanedItems]);
			}
		};

		buildBreadcrumbs();
	}, [location.pathname, menuRoutes, helpTopics]);

	const handleBreadCrumbNavigate = async (
		breadcrumb: any,
		position: any = null
	) => {
		try {
			await context?.handlePageChange();
			sessionStorage.removeItem("main-tabkey");
			sessionStorage.removeItem("admin-tabkey");
			sessionStorage.removeItem("secondary-tabkey");
			sessionStorage.removeItem("tabkey");
			navigate(breadcrumb.href);
		} catch {}
	};

	const itemRender = (
		currentRoute: any,
		params: any,
		items: any,
		paths: any
	) => {
		const isLast = currentRoute?.index === items[items.length - 1].index;

		// Convert the currentRoute title to a shorter version if it is too long
		const routeNameLength = currentRoute.title.length;
		const showEllipsis = routeNameLength > 20;
		let displayText = currentRoute.title;
		if (showEllipsis) {
			displayText = currentRoute.title.slice(0, 20) + "...";
		}

		return isLast ? (
			<Tooltip title={showEllipsis ? currentRoute.title : ""}>
				<span
					className={`text-(--default-btn-color) px-2 py-1 rounded-sm ${
						localStorage.getItem("theme") === "dark"
							? "bg-white/20"
							: "bg-(--default-btn-color)/10"
					}`}
					onContextMenu={(event: React.MouseEvent<HTMLButtonElement>) =>
						handleContextMenu(event, { link: window.location.href })
					}
				>
					{displayText}
				</span>
			</Tooltip>
		) : (
			<Tooltip title={showEllipsis ? currentRoute.title : ""}>
				<a
					className={`${
						localStorage.getItem("theme") === "dark"
							? "text-white!"
							: "text-black!"
					}`}
					onClick={() => {
						handleBreadCrumbNavigate(currentRoute);
					}}
					onContextMenu={(event: any) => {
						const location = window.location.origin + currentRoute.href;
						handleContextMenu(event, { link: location });
					}}
				>
					{displayText}
				</a>
			</Tooltip>
		);
	};

	return (
		<>
			<Breadcrumb
				key={"Breadcrumb"}
				items={breadCrumb}
				itemRender={itemRender}
			/>
		</>
	);
}

export default BreadcrumbComponent;
